import { z } from 'zod';

import { request } from '@/lib/api';

/* ---------------------------------------------------------------------------
   העוזר — חוזה ה-API (src/modules/assistant בשרת).
   --------------------------------------------------------------------------- */

export const actionKindSchema = z.enum([
  'quote',
  'customer',
  'task',
  'price_items',
  'job_type',
  'team_member',
  'business_details',
  'finish_setup',
  'workflow',
]);
export type ActionKind = z.infer<typeof actionKindSchema>;

export const actionSchema = z.object({
  id: z.string(),
  kind: actionKindSchema,
  status: z.enum(['PENDING', 'CONFIRMED', 'CANCELLED', 'FAILED']),
  payload: z.record(z.unknown()),
  preview: z.record(z.unknown()),
  result: z
    .object({
      entityType: z.string(),
      entityId: z.string().optional(),
      href: z.string().optional(),
      label: z.string().optional(),
    })
    .passthrough()
    .nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
  canConfirm: z.boolean(),
});
export type AssistantAction = z.infer<typeof actionSchema>;

const attachmentSchema = z.object({
  name: z.string(),
  mime: z.string(),
  size: z.number(),
  kind: z.enum(['image', 'document', 'audio']),
});
export type AttachmentMeta = z.infer<typeof attachmentSchema>;

export const messageSchema = z.object({
  id: z.string(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  attachments: z.array(attachmentSchema),
  links: z.array(z.object({ kind: z.string(), href: z.string() })),
  transcribed: z.boolean(),
  createdAt: z.string(),
  actions: z.array(actionSchema),
});
export type AssistantMessage = z.infer<typeof messageSchema>;

export const conversationSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  mode: z.enum(['ONBOARDING', 'WORK']),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Conversation = z.infer<typeof conversationSchema>;

const statusSchema = z.object({
  available: z.boolean(),
  voice: z.boolean(),
  learning: z.boolean(),
});

/** איפה המשתמש נמצא — "תכין הצעה ללקוח הזה". השרת בודק את המזהה מול ה-DB. */
export interface PageContext {
  path?: string;
  entityType?: 'customer' | 'task' | 'quote';
  entityId?: string;
}

export interface SendInput {
  text?: string;
  audio?: Blob;
  files?: File[];
  context?: PageContext;
}

const inviteSchema = z
  .object({
    temporaryPassword: z.string(),
    loginUrl: z.string(),
    message: z.string(),
    waUrl: z.string().nullable(),
  })
  .optional();

export const assistantApi = {
  status: (signal?: AbortSignal) => request('/assistant/status', { schema: statusSchema, signal }),

  conversations: (signal?: AbortSignal) =>
    request('/assistant/conversations', { schema: z.array(conversationSchema), signal }),

  create: (mode: 'WORK' | 'ONBOARDING' = 'WORK') =>
    request('/assistant/conversations', {
      method: 'POST',
      body: { mode },
      schema: conversationSchema,
    }),

  remove: (id: string) => request(`/assistant/conversations/${id}`, { method: 'DELETE' }),

  messages: (id: string, signal?: AbortSignal) =>
    request(`/assistant/conversations/${id}/messages`, {
      schema: z.object({ conversation: conversationSchema, messages: z.array(messageSchema) }),
      signal,
    }),

  send: (id: string, input: SendInput) => {
    const form = new FormData();
    if (input.text) form.append('text', input.text);
    if (input.context) form.append('context', JSON.stringify(input.context));
    if (input.audio) form.append('audio', input.audio, `voice.${extensionOf(input.audio.type)}`);
    for (const f of input.files ?? []) form.append('files', f, f.name);
    return request(`/assistant/conversations/${id}/messages`, {
      method: 'POST',
      body: form,
      schema: z.object({
        conversation: conversationSchema,
        userMessage: messageSchema,
        assistantMessage: messageSchema,
      }),
    });
  },

  confirm: (actionId: string) =>
    request(`/assistant/actions/${actionId}/confirm`, {
      method: 'POST',
      schema: z.object({ action: actionSchema, invite: inviteSchema }),
    }),

  cancel: (actionId: string) =>
    request(`/assistant/actions/${actionId}/cancel`, { method: 'POST', schema: actionSchema }),

  edit: (actionId: string, payload: Record<string, unknown>) =>
    request(`/assistant/actions/${actionId}`, {
      method: 'PATCH',
      body: { payload },
      schema: actionSchema,
    }),
};

function extensionOf(mime: string): string {
  if (mime.includes('mp4') || mime.includes('aac')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
}
